import type { BoardLane, RosterState } from '../api/work'

/* **THE SHAPE THESE RULES ACTUALLY NEED, AND NOTHING MORE.**

   Two screens ask these questions about two different records — the assignment board's
   `BoardLane` and the supervisor's `SupervisorLane`, which carry different cards for
   different purposes. Typing the rules against either one would have forced the other to
   pretend to be it, and a record that pretends is a record somebody eventually fills in
   with placeholder fields to get past the compiler.

   So the rules take the three things they read: whether he is working, what he was given,
   and what he is holding. */
export interface WorkloadTask { readonly credits: number }

export interface WorkloadLane {
  readonly rosterState: string
  readonly creditBudget: number | null
  readonly tasks: readonly WorkloadTask[]
}

/* ============================================================================
   laneWorkload.ts — the figure under every lane, and the warning above the board.

   **IT LIVES IN THE SHARED CORE, NOT ON THE WEB.** It sat in `app/web/src/screens` until
   Sprint 45, which was right while one screen read it and wrong the moment a second did:
   the supervisor's handset (M-12) draws the same judgement — over budget, free, on track —
   about the same lanes, and a second implementation of it would be two opinions about one
   fact. The first day they disagreed, a supervisor would be told a man was over on the
   board and free on the phone, and would believe neither screen again.

   THE WORKLOAD IS THE SUM OF THE CARDS. Not a number sent alongside them, not a
   number cached from the last refresh — the sum of exactly the array the lane renders.
   It is the same invariant as the filter chips on the room board, for the same reason:
   a lane reading "6.5 cr" above cards adding to 7.5 destroys a supervisor's trust in
   the whole screen, and they will not tell you, they will just stop using it.
   ========================================================================= */

/** Credits are DECIMAL(4,2) on the wire. Summing in binary floating point gives
 *  6.500000000000001 on a bad day, which then renders as 6.5 but compares as over
 *  budget. Work in hundredths and divide once. */
export function sumCredits(tasks: readonly WorkloadTask[]): number {
  const hundredths = tasks.reduce((total, t) => total + Math.round(t.credits * 100), 0)
  return hundredths / 100
}

export const workloadOf = (lane: WorkloadLane): number => sumCredits(lane.tasks)

export const canReceiveWork = (state: RosterState | string): boolean => state === 'ON_SHIFT'

export interface BalanceAdvice {
  /** The lane that is over its budget, and by how much. */
  over: { lane: BoardLane; by: number }
  /** The lane with the most room, if there is one. Null means nobody has capacity. */
  freest: { lane: BoardLane; free: number } | null
}

/**
 * The line above the board: who is over, and who specifically has room.
 *
 * It names a person rather than saying "unbalanced" because a supervisor should not
 * have to scan four lanes to find the fix. Only ON SHIFT lanes are considered on both
 * sides — somebody on a rest day is not over budget and is not spare capacity.
 *
 * Returns the WORST case only. Three warnings at once is a wall a supervisor reads as
 * noise; one actionable sentence is a thing they do.
 */
export function balanceAdvice(lanes: readonly BoardLane[]): BalanceAdvice | null {
  const working = lanes.filter(
    (l) => canReceiveWork(l.rosterState) && l.creditBudget !== null)

  const over = working
    .map((lane) => ({ lane, by: round2(workloadOf(lane) - lane.creditBudget!) }))
    .filter((x) => x.by > 0)
    .sort((a, b) => b.by - a.by)[0]

  if (!over) return null

  const freest = working
    .filter((l) => l.userId !== over.lane.userId)
    .map((lane) => ({ lane, free: round2(lane.creditBudget! - workloadOf(lane)) }))
    .filter((x) => x.free > 0)
    .sort((a, b) => b.free - a.free)[0]

  return { over, freest: freest ?? null }
}

const round2 = (n: number) => Math.round(n * 100) / 100

/**
 * **WHERE A LANE STANDS, IN ONE WORD.** Sprint 45, for M-12.
 *
 * Pack 6 puts a chip on every card of the supervisor's day — *"OK"*, *"Over"*, *"Free"* —
 * beside the figures they summarise. It is derived here rather than sent, because it is a
 * judgement about the cards and the cards are what the screen draws; a chip sent from the
 * server beside a figure the client sums is the same two-numbers-for-one-fact mistake
 * `workloadOf` exists to avoid.
 *
 * `OFF` is somebody who is not working today — a rest day, leave, an absence. Their lane is
 * drawn visible and inert, the way the board draws it: an absence is a fact a supervisor
 * needs, not a gap where they expected a person.
 */
export type LaneStanding = 'OVER' | 'FREE' | 'ON_TRACK' | 'OFF'

/**
 * **A QUARTER OF THE SHIFT STILL UNALLOCATED IS "FREE". LESS THAN THAT IS "ON TRACK".**
 *
 * The threshold is the part worth arguing about, and the reasoning is this: a lane with one
 * room's worth of slack is not spare capacity — the morning absorbs it, a fault appears, a
 * guest asks for a late check-out — and a supervisor told to move work there is being sent
 * on an errand that makes the board worse. A quarter of a shift is two hours on the
 * client's six-hour shifts, which is capacity somebody can actually be given.
 *
 * It is deliberately NOT "could take one more room": a room's credits differ by type, so
 * that rule would call the same lane free or not depending on which room was asked about,
 * and the chip is about the LANE.
 */
export const FREE_FRACTION = 0.25

export function laneStanding(lane: WorkloadLane): LaneStanding {
  if (!canReceiveWork(lane.rosterState) || lane.creditBudget === null) return 'OFF'

  const spare = round2(lane.creditBudget - workloadOf(lane))

  /* OVER FIRST, AND STRICTLY. Exactly at budget is on track — a man given precisely his
     shift has been given precisely his shift, and drawing that in the warning colour
     teaches a supervisor that the colour means nothing. */
  if (spare < 0) return 'OVER'
  return spare >= round2(lane.creditBudget * FREE_FRACTION) ? 'FREE' : 'ON_TRACK'
}

/* ============================================================================
   WHO SHOULD TAKE THIS ROOM — M-14, Sprint 47. Floor preference CQ-141, Sprint 52.
   ========================================================================= */

/**
 * A candidate for a room, which knows whether it is already working the room's floor.
 *
 * **THE FLAG IS REQUIRED, NOT OPTIONAL** — CQ-141. An optional `onThisFloor` would let a
 * future caller pass lanes that say nothing and quietly receive the ranking for *nobody is
 * on the floor*, which is a different recommendation wearing the same face. A caller that
 * genuinely has no floor to compare against says so by passing `false` everywhere, and
 * then this behaves exactly as it did before the client answered.
 */
export interface FloorAwareLane extends WorkloadLane {
  readonly onThisFloor: boolean
}

/**
 * **THE ONE WITH THE MOST ROOM LEFT, AND NOBODY IF NOBODY HAS ANY.**
 *
 * M-14 recommends a person rather than presenting a list — pack 6's own words — because
 * of the moment it is opened in: a guest is due at eleven, the supervisor is standing in
 * a corridor, and four names with four numbers beside them is a small arithmetic problem
 * handed to a man who has one minute.
 *
 * **IT IS DERIVED HERE, FROM THE CARDS THE SCREEN DRAWS**, and not sent by the server.
 * The same rule as `laneStanding`: a recommendation sent beside a workload the client
 * sums is two opinions about one fact, and the first morning they disagreed — a card
 * raised between the two queries would do it — the button would name somebody the list
 * had just shown as full.
 *
 * **NULL WHEN EVERYBODY IS OVER**, rather than the least-over. A button reading *"Move to
 * Fatima"* when Fatima is at nine of eight credits is the screen recommending a mistake,
 * and a supervisor who takes that advice twice stops reading the numbers under the names.
 * With nobody free he still has the whole list and can overrule; what he does not get is
 * the product telling him to.
 *
 * ── AND THE FLOOR COMES FIRST — CQ-141, ANSWERED 17 SEPTEMBER ──────────────────
 * **AMONG CANDIDATES WHO HAVE ROOM, WHOEVER IS ALREADY ON THAT FLOOR WINS.** The client's
 * own words: *"a pair two floors away is recommended only when nobody on the floor has
 * capacity"*. So the floor is not a tie-breaker applied after the credits — it is the
 * FIRST comparison, and a pair on the floor with half a credit spare beats a pair two
 * floors away with four.
 *
 * **WHAT STOOD HERE WAS A REFUSAL, AND IT WAS AN HONEST ONE.** Sprint 47 declined to rank
 * by the floor because the product has no measure of what a walk costs, and inventing one
 * would have put a fabricated number in front of a supervisor. That reasoning was right
 * and it is why this was ASKED rather than guessed. The answer needs no such number: it is
 * an ordering, not an arithmetic — *on the floor* before *not on the floor* — and an
 * ordering is something the client can simply state, which is what they did.
 *
 * The candidate must still have room. This never recommends somebody at their budget for
 * being conveniently placed; being nearby is not capacity.
 */
export function bestFitFor<T extends FloorAwareLane>(candidates: readonly T[]): T | null {
  const withRoom = candidates
    .filter((c) => canReceiveWork(c.rosterState) && c.creditBudget !== null)
    .map((lane) => ({ lane, spare: round2(lane.creditBudget! - workloadOf(lane)) }))
    .filter((x) => x.spare > 0)

  if (withRoom.length === 0) return null

  /* THE FLOOR, THEN THE ROOM LEFT, THEN THE BIGGER SHIFT.
     TIES BROKEN BY THE BIGGER SHIFT, not by the order the server happened to send. Two
     lanes with two credits spare are not equally free if one of them is a pair on two
     six-hour shifts — and a recommendation that changed between refreshes for no visible
     reason is one a supervisor learns to distrust. */
  const sorted = [...withRoom].sort((a, b) =>
    Number(b.lane.onThisFloor) - Number(a.lane.onThisFloor)
    || b.spare - a.spare
    || (b.lane.creditBudget! - a.lane.creditBudget!))

  return sorted[0]!.lane
}
